import { useEffect, useRef } from 'react';
import { Link } from '@tanstack/react-router';
import { useFormContext } from 'react-hook-form';
import { UserPlus } from 'lucide-react';
import {
  Button,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  ErrorMessage,
  Spinner,
} from '@finmin/ui';
import type { LoginPhoneValues } from '../types';
import { PhoneField } from './phone-field';

interface PhoneStepProps {
  isPending: boolean;
  apiError: string | null;
  /** When the API rejected the number because no customer exists with it. */
  notFound?: boolean;
  onSubmit: (values: LoginPhoneValues) => void;
}

/**
 * Login step 1 — phone number entry.
 * Receives the RHF form via context (caller wraps with <Form {...form}>).
 */
export function PhoneStep({ isPending, apiError, notFound, onSubmit }: PhoneStepProps) {
  const form = useFormContext<LoginPhoneValues>();
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Auto-focus on mount
  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 120);
    return () => clearTimeout(t);
  }, []);

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="space-y-6">
      {/* Heading */}
      <div className="space-y-1">
        <h2 className="font-display font-medium text-2xl tracking-tight text-foreground">Welcome back.</h2>
        <p className="text-sm text-muted-foreground">
          Enter your mobile number to receive a one-time code.
        </p>
      </div>

      {/* Phone field */}
      <FormField
        control={form.control}
        name="mobile_e164"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>
              Mobile number{' '}
              <span className="text-destructive" aria-label="required">*</span>
            </FormLabel>
            <FormControl>
              <PhoneField
                value={field.value}
                onChange={(val) => field.onChange(val)}
                ref={(el) => {
                  field.ref(el);
                  inputRef.current = el;
                }}
                onBlur={field.onBlur}
                name={field.name}
                id={field.name}
                hasError={!!fieldState.error}
                disabled={isPending}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* API-level error */}
      {notFound ? (
        <div
          className="rounded-xl border p-4 flex items-start gap-3"
          style={{
            backgroundColor: 'hsl(var(--palette-mint, 152 41% 92%))',
            borderColor:     'hsl(var(--palette-forest, 176 72% 17%) / 0.3)',
          }}
        >
          <div
            className="rounded-full p-2 shrink-0"
            style={{ backgroundColor: 'hsl(var(--palette-forest, 176 72% 17%) / 0.12)' }}
          >
            <UserPlus
              className="h-4 w-4"
              style={{ color: 'hsl(var(--palette-forest, 176 72% 17%))' }}
              aria-hidden
            />
          </div>
          <div className="flex-1 text-sm">
            <p className="font-medium text-foreground">First Time? Please Sign-Up</p>
            <p className="text-muted-foreground mt-0.5">
              We couldn't find an account for that mobile number. Create one in
              a minute to get started.
            </p>
            <Link
              to="/signup"
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold underline underline-offset-4"
              style={{ color: 'hsl(var(--palette-forest, 176 72% 17%))' }}
            >
              Sign up now →
            </Link>
          </div>
        </div>
      ) : (
        <ErrorMessage message={apiError} />
      )}

      {/* Submit */}
      <Button type="submit" className="w-full gap-2" size="lg" disabled={isPending}>
        {isPending ? (
          <>
            <Spinner className="h-4 w-4" />
            Sending code…
          </>
        ) : (
          'Send OTP'
        )}
      </Button>

      {/* Sign-up link */}
      <p className="text-sm text-center text-muted-foreground">
        Don&apos;t have an account?{' '}
        <Link
          to="/signup"
          className="font-medium text-primary hover:underline underline-offset-4 transition-colors"
        >
          Sign Up
        </Link>
      </p>
    </form>
  );
}
